import type { JobMeta } from '../fixtures/domain.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './JobDetailsDialog.module.css';

/**
 * The full job posting, on demand. The panel's Job section shows only the
 * summary an employer needs at a glance; this dialog carries the description,
 * responsibilities and requirements for when they've forgotten which role a
 * conversation is about. Read-only — nothing here is editable.
 */
/** The fields the dialog reads; a `JobMeta` and a mapped `JobDetailDto` both satisfy it. */
export type JobDetailsSource = Pick<
  JobMeta,
  'jobTitle' | 'jobType' | 'jobLocation' | 'description' | 'responsibilities' | 'requirements'
>;

export function JobDetailsDialog({ job, onClose }: { job: JobDetailsSource; onClose: () => void }) {
  useDismiss(true, onClose);

  const meta = [job.jobType, job.jobLocation].filter(Boolean).join(' · ');

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={job.jobTitle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.heading}>
            <h2 className={styles.title}>{job.jobTitle}</h2>
            {meta && <p className={styles.meta}>{meta}</p>}
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close job details"
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className={styles.body}>
          {job.description && <p className={styles.description}>{job.description}</p>}
          {job.responsibilities && job.responsibilities.length > 0 && (
            <JobList title="Responsibilities" items={job.responsibilities} />
          )}
          {job.requirements && job.requirements.length > 0 && (
            <JobList title="Requirements" items={job.requirements} />
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function JobList({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item} className={styles.listItem}>
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}
